import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2, Package } from "lucide-react";
import { API } from "@/App";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function proxyImg(url) {
  if (!url) return null;
  if (url.includes("cdn.shopify.com")) {
    return `${BACKEND}/api/solm/image-proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
}

/**
 * Unified marketplace search — searches Aspen Surgical + all Sol-M categories
 * by product name, parent SKU, or variant SKU. Results link directly to the
 * correct page with ?product=<id> deep link so the detail modal auto-opens.
 */
export default function MarketplaceSearchBar({ className = "", placeholder = "Search by name, SKU, or item number..." }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [activeIdx, setActiveIdx] = useState(-1);
  const navigate = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]); setTotal(0); return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/marketplace/search?q=${encodeURIComponent(q)}&limit=20`);
        const data = await r.json();
        setResults(data.results || []);
        setTotal(data.total || 0);
        setOpen(true);
        setActiveIdx(-1);
      } catch {
        setResults([]);
      } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const handleClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const goTo = (r) => {
    setOpen(false);
    setQ("");
    navigate(r.route);
  };

  const onKeyDown = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const r = activeIdx >= 0 ? results[activeIdx] : results[0];
      if (r) goTo(r);
    } else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={boxRef} className={`relative ${className}`} data-testid="marketplace-search-bar">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => { if (results.length) setOpen(true); }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="w-full pl-9 pr-9 h-10 rounded-lg bg-slate-900/60 border border-slate-700 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400 focus:bg-slate-900"
          data-testid="marketplace-search-input"
        />
        {loading ? (
          <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 animate-spin" />
        ) : q ? (
          <button
            type="button"
            onClick={() => { setQ(""); setOpen(false); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-white"
            aria-label="Clear"
            data-testid="marketplace-search-clear">
            <X className="w-3.5 h-3.5" />
          </button>
        ) : null}
      </div>

      {open && q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-[#0b1d3a] border border-slate-700 rounded-xl shadow-2xl z-50 max-h-[70vh] overflow-y-auto" data-testid="marketplace-search-results">
          {results.length === 0 && !loading ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400">
              <Package className="w-6 h-6 mx-auto mb-2 text-slate-600" />
              No matches for &quot;{q}&quot;
            </div>
          ) : (
            <>
              <div className="px-4 py-2 text-[10px] font-semibold text-cyan-300 uppercase tracking-wider bg-slate-900/50 border-b border-slate-800">
                {total} result{total === 1 ? "" : "s"} across Surgical &amp; Sol-M
              </div>
              {results.map((r, i) => (
                <button
                  key={r.id + (r.matched_variant?.erp || "")}
                  onClick={() => goTo(r)}
                  onMouseEnter={() => setActiveIdx(i)}
                  className={`w-full flex items-center gap-3 p-3 border-b border-slate-800/70 text-left transition-colors ${activeIdx === i ? "bg-slate-800/80" : "hover:bg-slate-800/60"}`}
                  data-testid={`search-result-${r.erp}`}
                >
                  <div className="w-12 h-12 bg-white rounded flex items-center justify-center shrink-0">
                    {r.image
                      ? <img src={proxyImg(r.image)} alt="" className="max-w-full max-h-full object-contain" />
                      : <Package className="w-6 h-6 text-slate-300" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${r.source === "Aspen Surgical" ? "bg-cyan-500/15 text-cyan-300" : "bg-amber-500/15 text-amber-300"}`}>
                        {r.source}
                      </span>
                      <span className="text-[10px] text-slate-500">{r.category}</span>
                    </div>
                    <p className="text-sm text-white font-medium truncate">{r.name}</p>
                    <p className="text-[11px] font-mono text-slate-400">
                      SKU: {r.erp}
                      {r.matched_variant && r.matched_variant.erp !== r.erp && (
                        <> · matched variant <span className="text-amber-300">{r.matched_variant.erp}</span></>
                      )}
                      {r.variant_count > 1 && <> · {r.variant_count} sizes</>}
                    </p>
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
